import type { Notification } from 'src/context/api/data/api';

// Pure derivation helpers for the notification center.
// The server is the single source of truth: it returns only non-expired
// notifications already, but these helpers keep the front-end resilient
// (e.g. stale cached data, race between fetch and clock) by re-deriving
// visibility/unread-count/type filters from the same list.

// Timestamps with no timezone designator (no trailing `Z`, `GMT` or `+hh:mm`/
// `-hh:mm` offset) are treated as UTC, matching the backend's default. Flask
// serialises a datetime as RFC 822 ("Fri, 30 Oct 2026 10:00:00 GMT"): appending
// a `Z` to that one would make it unparseable.
const TZ_DESIGNATOR_RE = /(Z|GMT|[+-]\d{2}:?\d{2})$/;

export function toUtcTimestamp(value: string): number {
  const normalized = TZ_DESIGNATOR_RE.test(value) ? value : `${value}Z`;
  return new Date(normalized).getTime();
}

export function isVisible(n: Notification, now: Date = new Date()): boolean {
  if (!n.expires_at) return true;
  const expiresAt = toUtcTimestamp(n.expires_at);
  // An unparseable timestamp must not silently hide an otherwise valid
  // notification, so treat NaN as still visible.
  return Number.isNaN(expiresAt) || expiresAt > now.getTime();
}

export function filterVisible(list: Notification[], now: Date = new Date()): Notification[] {
  return list.filter((n) => isVisible(n, now));
}

export function countUnread(list: Notification[]): number {
  return list.filter((n) => n.status === 'unread').length;
}

export function filterByType(list: Notification[], type: Notification['type']): Notification[] {
  return list.filter((n) => n.type === type);
}

export function deriveNotificationView(
  list: Notification[],
  now: Date = new Date(),
): { visible: Notification[]; unreadCount: number } {
  const visible = filterVisible(list, now);
  return { visible, unreadCount: countUnread(visible) };
}
